import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Filter,
  Image as ImageIcon,
  LayoutGrid,
  List,
  Mail,
  Pencil,
  RefreshCw,
  Search,
} from "lucide-react";

import MetadataWorkspacePanel from "./MetadataWorkspacePanel";

const THREAD_PAGE_SIZE = 10;
const EMPTY_ATTACHMENTS = [];

function scrollToVisible(target, behavior = "smooth") {
  if (!target) return;

  const nestedScroller = target.closest("[data-thread-detail]");
  if (nestedScroller && nestedScroller !== target) {
    const nestedTop =
      target.getBoundingClientRect().top -
      nestedScroller.getBoundingClientRect().top +
      nestedScroller.scrollTop;
    nestedScroller.scrollTo({ top: Math.max(0, nestedTop - 8), behavior });
  }

  const scrollParent = target.closest(".app-shell__content");
  if (scrollParent) {
    const top =
      target.getBoundingClientRect().top -
      scrollParent.getBoundingClientRect().top +
      scrollParent.scrollTop;
    scrollParent.scrollTo({ top: Math.max(0, top - 8), behavior });
    return;
  }

  target.scrollIntoView({ block: "start", behavior });
}

function isVerifiedEmail(email) {
  return email?.verified_flag === true || email?.verified_flag === 1;
}

function formatListDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatFileSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileKind(file) {
  const name = String(file?.name || file?.path || "").toLowerCase();
  const type = String(file?.content_type || "").toLowerCase();
  if (type.includes("pdf") || name.endsWith(".pdf")) return "pdf";
  if (
    type.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|tif{1,2}|heic|heif)$/.test(name)
  ) {
    return "image";
  }
  return "file";
}

function formatEntryDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString();
}

function fitPreviewIframe(iframe) {
  try {
    const doc = iframe?.contentDocument;
    if (!doc) return;

    if (!doc.getElementById("cfp-preview-scroll")) {
      const style = doc.createElement("style");
      style.id = "cfp-preview-scroll";
      style.textContent = `
        html, body {
          overflow: hidden !important;
          height: auto !important;
          max-height: none !important;
        }
      `;
      (doc.head || doc.documentElement).appendChild(style);
    }

    const height = Math.max(
      doc.documentElement?.scrollHeight || 0,
      doc.body?.scrollHeight || 0,
    );
    if (height > 0) {
      iframe.style.height = `${height}px`;
    }
  } catch {
    // Cross-origin previews keep native iframe scrolling.
  }
}

function fileIconColors(kind) {
  if (kind === "pdf") {
    return { background: "#fee2e2", color: "#dc2626" };
  }
  if (kind === "image") {
    return { background: "#dcfce7", color: "#16a34a" };
  }
  return { background: "#dbeafe", color: "#2563eb" };
}

function StatusPill({ verified }) {
  return (
    <span
      style={{
        ...styles.pill,
        ...(verified ? styles.pillVerified : styles.pillPending),
      }}
    >
      {verified ? "Verified" : "Pending"}
    </span>
  );
}

function searchableEmailText(email) {
  if (!email) return "";
  return [
    email.email_subject,
    email.mail_from_address,
    email.mail_from_name,
    email.sender_name,
    email.email_body,
    email.body,
    email.body_preview,
    email.snippet,
    email.email_content,
    email.content,
    email.text_body,
  ]
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function emailMatchesSearch(email, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  return searchableEmailText(email).includes(needle);
}

const DEFAULT_THREAD_FILTERS = {
  status: "all",
  attachments: "any",
  datePreset: "any",
  dateFrom: "",
  dateTo: "",
  sender: "",
};

function startOfLocalDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function endOfLocalDay(value) {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date.getTime();
}

function emailMatchesThreadFilters(email, filters) {
  if (filters.status === "review" && isVerifiedEmail(email)) return false;

  const attachmentCount = (email.attachments || []).length;
  if (filters.attachments === "has" && attachmentCount === 0) return false;
  if (filters.attachments === "none" && attachmentCount > 0) return false;

  const senderNeedle = String(filters.sender || "").trim().toLowerCase();
  if (senderNeedle) {
    const senderText = `${email.mail_from_address || ""} ${email.mail_from_name || ""}`.toLowerCase();
    if (!senderText.includes(senderNeedle)) return false;
  }

  if (filters.datePreset && filters.datePreset !== "any") {
    const timestamp = new Date(email.created_at).getTime();
    if (!Number.isFinite(timestamp)) return false;
    const now = Date.now();
    if (filters.datePreset === "today") {
      if (timestamp < startOfLocalDay(now) || timestamp > endOfLocalDay(now)) {
        return false;
      }
    } else if (filters.datePreset === "7d") {
      if (timestamp < now - 7 * 24 * 60 * 60 * 1000) return false;
    } else if (filters.datePreset === "30d") {
      if (timestamp < now - 30 * 24 * 60 * 60 * 1000) return false;
    } else if (filters.datePreset === "custom") {
      if (filters.dateFrom && timestamp < startOfLocalDay(filters.dateFrom)) {
        return false;
      }
      if (filters.dateTo && timestamp > endOfLocalDay(filters.dateTo)) {
        return false;
      }
    }
  }

  return true;
}

function hasActiveThreadFilters(filters) {
  return (
    filters.status !== "all" ||
    filters.attachments !== "any" ||
    filters.datePreset !== "any" ||
    Boolean(String(filters.dateFrom || "").trim()) ||
    Boolean(String(filters.dateTo || "").trim()) ||
    Boolean(String(filters.sender || "").trim())
  );
}

function FilterChip({ active, children, onClick, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel || (typeof children === "string" ? children : undefined)}
      style={{
        ...styles.filterChip,
        ...(active ? styles.filterChipActive : {}),
      }}
    >
      {children}
    </button>
  );
}

function ThreadAdvancedFilters({
  open,
  anchorRef,
  draft,
  setDraft,
  onApply,
  onClear,
  onClose,
}) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: Math.max(12, window.innerWidth - rect.right),
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !position) return null;

  const setField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Advanced filters"
      style={{
        ...styles.filtersPopover,
        top: position.top,
        right: position.right,
      }}
    >
      <div style={styles.filtersTitle}>Advanced filters</div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>STATUS</div>
        <div style={styles.filterChipRow}>
          <FilterChip
            active={draft.status === "all"}
            onClick={() => setField("status", "all")}
          >
            All
          </FilterChip>
          <FilterChip
            active={draft.status === "review"}
            onClick={() => setField("status", "review")}
          >
            Pending
          </FilterChip>
        </div>
      </div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>ATTACHMENTS</div>
        <div style={styles.filterChipRow}>
          <FilterChip
            active={draft.attachments === "any"}
            onClick={() => setField("attachments", "any")}
          >
            Any
          </FilterChip>
          <FilterChip
            active={draft.attachments === "has"}
            onClick={() => setField("attachments", "has")}
          >
            Has attachments
          </FilterChip>
          <FilterChip
            active={draft.attachments === "none"}
            onClick={() => setField("attachments", "none")}
          >
            No attachments
          </FilterChip>
        </div>
      </div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>DATE RECEIVED</div>
        {[
          ["any", "Any time"],
          ["today", "Today"],
          ["7d", "Last 7 days"],
          ["30d", "Last 30 days"],
          ["custom", "Custom date range"],
        ].map(([value, label]) => (
          <label key={value} style={styles.filtersRadio}>
            <input
              type="radio"
              name="v2-thread-date-preset"
              checked={draft.datePreset === value}
              onChange={() => setField("datePreset", value)}
            />
            {label}
          </label>
        ))}
        {draft.datePreset === "custom" ? (
          <div style={styles.filtersDateRow}>
            <input
              type="date"
              aria-label="From date"
              value={draft.dateFrom}
              onChange={(event) => setField("dateFrom", event.target.value)}
              style={styles.filtersDateInput}
            />
            <input
              type="date"
              aria-label="To date"
              value={draft.dateTo}
              onChange={(event) => setField("dateTo", event.target.value)}
              style={styles.filtersDateInput}
            />
          </div>
        ) : null}
      </div>

      <div style={styles.filtersSection}>
        <div style={styles.filtersSectionLabel}>SENDER</div>
        <div style={styles.threadSearchWrap}>
          <Search size={14} style={styles.threadSearchIcon} />
          <input
            type="search"
            value={draft.sender}
            onChange={(event) => setField("sender", event.target.value)}
            placeholder="Search sender..."
            aria-label="Search sender"
            style={styles.threadSearchInput}
          />
        </div>
      </div>

      <div style={styles.filtersFooter}>
        <button type="button" style={styles.filtersClear} onClick={onClear}>
          Clear filters
        </button>
        <button type="button" style={styles.filtersApply} onClick={onApply}>
          Apply
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default function InboxReviewThreadView({
  thread,
  activeTab,
  selectedMsgId,
  selectedRow,
  reviewMode,
  isNarrow,
  previewLoading,
  previewUrl,
  previewKind,
  previewError,
  onSelectEmail,
  onCollapse,
  onEdit,
  onReindex,
  onPreview,
  onOpenAll,
  onDownload,
  onCloseReview,
  onSaved,
}) {
  const [sortOrder, setSortOrder] = useState("newest");
  const [listPage, setListPage] = useState(1);
  const [threadSearch, setThreadSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_THREAD_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_THREAD_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detailTab, setDetailTab] = useState("preview");
  const [attachLayout, setAttachLayout] = useState("grid");
  const [previewScrollNonce, setPreviewScrollNonce] = useState(0);
  const [previewedPath, setPreviewedPath] = useState(null);
  const previewFrameRef = useRef(null);
  const detailRef = useRef(null);
  const filtersBtnRef = useRef(null);

  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  const emails = useMemo(() => {
    const list = [...(thread?.emails || [])];
    list.sort((a, b) => {
      const timeA = new Date(a.created_at).getTime();
      const timeB = new Date(b.created_at).getTime();
      return sortOrder === "newest" ? timeB - timeA : timeA - timeB;
    });
    return list;
  }, [thread, sortOrder]);

  const filteredEmails = useMemo(
    () =>
      emails.filter(
        (email) =>
          emailMatchesThreadFilters(email, appliedFilters) &&
          emailMatchesSearch(email, threadSearch),
      ),
    [emails, appliedFilters, threadSearch],
  );

  const totalEmails = emails.length;
  const filteredCount = filteredEmails.length;
  const totalPages = Math.max(1, Math.ceil(filteredCount / THREAD_PAGE_SIZE));
  const showPager = filteredCount > THREAD_PAGE_SIZE;
  const safePage = Math.min(Math.max(1, listPage), totalPages);
  const pagedEmails = filteredEmails.slice(
    (safePage - 1) * THREAD_PAGE_SIZE,
    safePage * THREAD_PAGE_SIZE,
  );
  const filtersActive = hasActiveThreadFilters(appliedFilters);
  const hasQuery = Boolean(threadSearch.trim()) || filtersActive;
  const showThreadSearch = totalEmails > 1;
  const showThreadFilters = totalEmails > 1;

  const selectedEmail =
    emails.find((email) => email.msg_id === selectedMsgId) || emails[0];
  const selectedIndex = emails.findIndex(
    (email) => email.msg_id === selectedEmail?.msg_id,
  );
  const selectedNumber = selectedIndex >= 0 ? selectedIndex + 1 : 0;
  const visibleDetailTab =
    detailTab === "attachments" || detailTab === "metadata"
      ? detailTab
      : "preview";
  const attachments = selectedEmail?.attachments || EMPTY_ATTACHMENTS;
  const previewableAttachments = useMemo(
    () => attachments.filter((file) => file?.path),
    [attachments],
  );
  const attachmentIndex = previewableAttachments.findIndex(
    (file) => file.path === previewedPath,
  );
  const isAttachmentPreview = attachmentIndex >= 0;
  const canCycleAttachments =
    isAttachmentPreview && previewableAttachments.length > 1;
  const emailStoragePath = selectedEmail?.email_storage_path;
  const emailFile = useMemo(() => {
    if (!emailStoragePath) {
      return null;
    }
    return {
      name: String(emailStoragePath).split("/").pop(),
      path: emailStoragePath,
      type: "email",
    };
  }, [emailStoragePath]);
  const tabFiles = useMemo(() => {
    const files = [];
    if (emailFile) {
      files.push(emailFile);
    }
    files.push(...attachments);
    return files;
  }, [attachments, emailFile]);
  const tabFileCount = tabFiles.length;
  const verified = isVerifiedEmail(selectedEmail);
  const canEdit = activeTab === "pending" && !verified;
  const canReindex = activeTab === "verified";
  const editing =
    reviewMode && selectedRow?.msg_id === selectedEmail?.msg_id;

  useEffect(() => {
    if (selectedEmail?.email_storage_path) {
      setPreviewedPath(selectedEmail.email_storage_path);
      onPreview?.(selectedEmail.email_storage_path);
    } else {
      setPreviewedPath(null);
    }
  }, [
    onPreview,
    selectedEmail?.email_storage_path,
    selectedEmail?.msg_id,
  ]);

  useEffect(() => {
    setThreadSearch("");
    setAppliedFilters(DEFAULT_THREAD_FILTERS);
    setDraftFilters(DEFAULT_THREAD_FILTERS);
    setFiltersOpen(false);
    setListPage(1);
  }, [thread?.conversation_id]);

  useEffect(() => {
    setListPage(1);
  }, [threadSearch, appliedFilters, sortOrder]);

  useEffect(() => {
    const index = filteredEmails.findIndex(
      (email) => email.msg_id === selectedMsgId,
    );
    if (index < 0) return;
    const pageForSelected = Math.floor(index / THREAD_PAGE_SIZE) + 1;
    setListPage((current) =>
      current === pageForSelected ? current : pageForSelected,
    );
    // Only follow the selected email when the selection or thread changes.
    // Search/filter resets stay on page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMsgId, thread?.conversation_id]);

  useEffect(() => {
    setListPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useLayoutEffect(() => {
    if (detailRef.current) {
      detailRef.current.scrollTop = 0;
    }
  }, [selectedEmail?.msg_id]);

  useEffect(() => {
    setDetailTab("preview");
  }, [selectedEmail?.msg_id]);

  useLayoutEffect(() => {
    if (!previewScrollNonce) {
      return;
    }
    scrollToVisible(previewFrameRef.current, "smooth");
  }, [previewScrollNonce]);

  const showPreview = (path) => {
    setDetailTab("preview");
    setPreviewedPath(path);
    onPreview?.(path);
    setPreviewScrollNonce((count) => count + 1);
  };

  const showAttachmentAt = (index) => {
    const file = previewableAttachments[index];
    if (!file?.path) {
      return;
    }
    showPreview(file.path);
  };

  const showPreviousAttachment = () => {
    if (!canCycleAttachments) {
      return;
    }
    const previousIndex =
      (attachmentIndex - 1 + previewableAttachments.length) %
      previewableAttachments.length;
    showAttachmentAt(previousIndex);
  };

  const showNextAttachment = () => {
    if (!canCycleAttachments) {
      return;
    }
    const nextIndex = (attachmentIndex + 1) % previewableAttachments.length;
    showAttachmentAt(nextIndex);
  };

  const openAllAttachments = () => {
    onOpenAll?.(tabFiles);
  };

  const downloadAllAttachments = () => {
    tabFiles.forEach((file) => {
      if (file?.path) onDownload?.(file.path, file.name);
    });
  };

  const goToListPage = (nextPage) => {
    const page = Math.min(Math.max(1, nextPage), totalPages);
    setListPage(page);
    const email = filteredEmails[(page - 1) * THREAD_PAGE_SIZE];
    if (email && email.msg_id !== selectedEmail?.msg_id) {
      onSelectEmail(email);
    }
  };

  const detailHeader = (
    <div style={styles.detailHeader}>
      <div style={styles.detailTitleWrap}>
        <div style={styles.mailBadge}>
          <Mail size={18} color="#16a34a" />
        </div>
        <div>
          <div style={styles.detailEmailIndex}>
            Email {selectedNumber} of {totalEmails}
          </div>
          <div style={styles.detailSubject}>
            {selectedEmail?.email_subject || "No subject"}
          </div>
        </div>
      </div>
      {!editing && canEdit ? (
        <button
          type="button"
          style={styles.editBtn}
          onClick={() => onEdit(selectedEmail)}
        >
          <Pencil size={14} />
          Edit metadata
        </button>
      ) : !editing && canReindex ? (
        <button
          type="button"
          style={styles.editBtn}
          onClick={() => onReindex(selectedEmail.msg_id)}
        >
          <RefreshCw size={14} />
          ReIndex
        </button>
      ) : null}
    </div>
  );

  const tabsAndBody = (
    <>
      <div style={styles.tabs}>
        {[
          ["preview", "Preview"],
          [
            "attachments",
            `Attachments (${tabFileCount})`,
          ],
          ["metadata", "Metadata"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            style={{
              ...styles.tab,
              ...(visibleDetailTab === id ? styles.tabActive : {}),
            }}
            onClick={() => setDetailTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={styles.tabBody}>
        {visibleDetailTab === "preview" && (
          <div
            ref={previewFrameRef}
            className="scrollable"
            style={styles.previewFrame}
          >
            {previewLoading ? (
              <div style={styles.previewStatus}>Loading preview...</div>
            ) : previewError ? (
              <div style={styles.previewStatus}>{previewError}</div>
            ) : previewUrl ? (
              previewKind === "image" ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={styles.previewImage}
                />
              ) : (
                <iframe
                  key={previewUrl}
                  title="Preview"
                  src={previewUrl}
                  style={styles.previewIframe}
                  onLoad={(event) => fitPreviewIframe(event.currentTarget)}
                />
              )
            ) : (
              <div style={styles.previewStatus}>
                {emailFile
                  ? "Preview will appear here."
                  : "Original email file is not available for preview."}
              </div>
            )}
            {canCycleAttachments ? (
              <>
                <button
                  type="button"
                  style={{ ...styles.navArrow, ...styles.navArrowLeft }}
                  onClick={showPreviousAttachment}
                  title="Previous attachment"
                  aria-label="Previous attachment"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  style={{ ...styles.navArrow, ...styles.navArrowRight }}
                  onClick={showNextAttachment}
                  title="Next attachment"
                  aria-label="Next attachment"
                >
                  <ChevronRight size={20} />
                </button>
                <div style={styles.previewCounter}>
                  {attachmentIndex + 1} / {previewableAttachments.length}
                </div>
              </>
            ) : null}
          </div>
        )}

        {visibleDetailTab === "attachments" && (
          <div style={styles.attachPanel} className="scrollable">
            <div style={styles.attachHeader}>
              <div style={styles.attachTitle}>
                {tabFileCount} {tabFileCount === 1 ? "attachment" : "attachments"}
              </div>
              <div style={styles.attachHeaderRight}>
                {tabFileCount > 0 ? (
                  <div style={styles.attachActions}>
                    <button
                      type="button"
                      style={styles.textLink}
                      onClick={downloadAllAttachments}
                    >
                      <Download size={14} />
                      Download all
                    </button>
                    <button
                      type="button"
                      style={styles.textLink}
                      onClick={openAllAttachments}
                    >
                      <Eye size={14} />
                      Open all
                    </button>
                  </div>
                ) : null}
                <div style={styles.viewToggle} role="group" aria-label="Attachment layout">
                  <button
                    type="button"
                    style={{
                      ...styles.viewToggleBtn,
                      ...(attachLayout === "grid" ? styles.viewToggleBtnActive : {}),
                    }}
                    title="Grid view"
                    aria-pressed={attachLayout === "grid"}
                    onClick={() => setAttachLayout("grid")}
                  >
                    <LayoutGrid size={14} />
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.viewToggleBtn,
                      ...(attachLayout === "list" ? styles.viewToggleBtnActive : {}),
                    }}
                    title="List view"
                    aria-pressed={attachLayout === "list"}
                    onClick={() => setAttachLayout("list")}
                  >
                    <List size={14} />
                  </button>
                </div>
              </div>
            </div>
            {tabFileCount === 0 ? (
              <div style={styles.muted}>No attachments</div>
            ) : attachLayout === "grid" ? (
              <div
                style={{
                  ...styles.attachGrid,
                  ...(isNarrow ? styles.attachGridNarrow : {}),
                }}
              >
                {tabFiles.map((file, index) => (
                  <AttachmentGridCard
                    key={`${file.path || file.name}-${index}`}
                    file={file}
                    onPreview={showPreview}
                    previewLoading={previewLoading}
                  />
                ))}
              </div>
            ) : (
              tabFiles.map((file, index) => (
                <FileCard
                  key={`${file.path || file.name}-${index}`}
                  file={file}
                  onPreview={showPreview}
                  onDownload={onDownload}
                  previewLoading={previewLoading}
                />
              ))
            )}
          </div>
        )}

        {visibleDetailTab === "metadata" && (
          <div style={styles.metaPanel} className="scrollable">
            <MetadataCard
              label="Document type"
              value={selectedEmail?.document_type || "UNKNOWN"}
            />
            <MetadataCard
              label="Policy number"
              value={selectedEmail?.policy_number || "N/A"}
            />
            <MetadataCard label="LOB" value={selectedEmail?.lob || "—"} />
            <MetadataCard
              label="Agency no"
              value={selectedEmail?.agency_no || "—"}
            />
            <MetadataCard
              label="Primary insured"
              value={selectedEmail?.primary_insured || "—"}
            />
            <MetadataCard
              label="Entry date"
              value={formatEntryDate(selectedEmail?.created_at)}
            />
            <MetadataCard
              label="Processing status"
              value={verified ? "Verified" : "Pending"}
            />
            {verified ? (
              <>
                <MetadataCard
                  label="Verified by"
                  value={selectedEmail?.verified_by || "—"}
                />
                <MetadataCard
                  label="Verified at"
                  value={formatEntryDate(selectedEmail?.verified_at)}
                />
              </>
            ) : null}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div
      style={{
        ...styles.shell,
        ...(isNarrow ? styles.shellNarrow : {}),
      }}
    >
      <aside
        style={{
          ...styles.sidebar,
          ...(isNarrow ? styles.sidebarNarrow : {}),
        }}
      >
        <div style={styles.sidebarFill}>
        <div style={styles.sidebarHeader}>
          <div style={styles.headerRow}>
            <button
              type="button"
              style={styles.backBtn}
              onClick={onCollapse}
              title="Back to queue"
              aria-label="Back to queue"
            >
              <ArrowLeft size={16} />
            </button>
            <div style={styles.sidebarTitle}>Emails in this conversation</div>
            {showThreadFilters ? (
            <div style={styles.headerActions}>
              <select
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                style={styles.sortSelect}
                aria-label="Sort emails"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
              <button
                ref={filtersBtnRef}
                type="button"
                style={{
                  ...styles.filterBtn,
                  ...(filtersOpen || filtersActive
                    ? styles.filterBtnActive
                    : {}),
                }}
                title="Advanced filters"
                aria-label="Advanced filters"
                aria-expanded={filtersOpen}
                aria-haspopup="dialog"
                onClick={() => {
                  setDraftFilters(appliedFilters);
                  setFiltersOpen((open) => !open);
                }}
              >
                <Filter size={15} />
              </button>
              <ThreadAdvancedFilters
                open={filtersOpen}
                anchorRef={filtersBtnRef}
                draft={draftFilters}
                setDraft={setDraftFilters}
                onClose={closeFilters}
                onClear={() => {
                  setDraftFilters(DEFAULT_THREAD_FILTERS);
                  setAppliedFilters(DEFAULT_THREAD_FILTERS);
                  setFiltersOpen(false);
                }}
                onApply={() => {
                  setAppliedFilters(draftFilters);
                  setFiltersOpen(false);
                }}
              />
            </div>
            ) : null}
          </div>
          {showThreadSearch ? (
          <div style={styles.sidebarSearchWrap}>
            <Search size={14} style={styles.threadSearchIcon} />
            <input
              type="search"
              value={threadSearch}
              onChange={(event) => setThreadSearch(event.target.value)}
              placeholder="Search subject, sender, or email..."
              aria-label="Search subject, sender, or email"
              style={styles.threadSearchInput}
            />
          </div>
          ) : null}
        </div>
        <div style={styles.emailList} className="scrollable">
          {pagedEmails.length === 0 ? (
            <div style={styles.threadEmpty}>
              {hasQuery
                ? "No emails match the current filters."
                : "No emails in this thread."}
            </div>
          ) : (
            pagedEmails.map((email) => {
            const selected = email.msg_id === selectedEmail?.msg_id;
            return (
              <button
                key={email.msg_id}
                type="button"
                style={{
                  ...styles.emailCard,
                  ...(selected ? styles.emailCardSelected : {}),
                }}
                onClick={() => onSelectEmail(email)}
              >
                <div style={styles.emailCardBody}>
                  <div style={styles.emailCardTop}>
                    <div style={styles.emailCardTime}>
                      {formatListDate(email.created_at)}
                    </div>
                    <StatusPill verified={isVerifiedEmail(email)} />
                  </div>
                  <div style={styles.emailCardSubject}>
                    {email.email_subject || "No subject"}
                  </div>
                  <div style={styles.emailCardFrom}>
                    {email.mail_from_address || "—"}
                  </div>
                </div>
              </button>
            );
            })
          )}
        </div>
        <div style={styles.listFooter}>
          <div style={styles.listFooterCount}>
            {hasQuery
              ? `${filteredCount} of ${totalEmails} ${totalEmails === 1 ? "email" : "emails"}`
              : `${totalEmails} ${totalEmails === 1 ? "email" : "emails"} in thread`}
          </div>
          {showPager ? (
            <div style={styles.pager}>
              <button
                type="button"
                style={{
                  ...styles.pagerBtn,
                  ...(safePage <= 1 ? styles.pagerBtnDisabled : {}),
                }}
                disabled={safePage <= 1}
                onClick={() => goToListPage(safePage - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft size={14} />
                Prev
              </button>
              <span style={styles.pagerCurrent} aria-current="page">
                {safePage}
              </span>
              <button
                type="button"
                style={{
                  ...styles.pagerBtn,
                  ...(safePage >= totalPages ? styles.pagerBtnDisabled : {}),
                }}
                disabled={safePage >= totalPages}
                onClick={() => goToListPage(safePage + 1)}
                aria-label="Next page"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          ) : null}
        </div>
        </div>
      </aside>

      <section ref={detailRef} data-thread-detail style={styles.detail}>
        {!selectedEmail ? (
          <div style={styles.emptyDetail}>Select an email in this conversation.</div>
        ) : editing ? (
          <div
            style={{
              ...styles.editLayout,
              ...(isNarrow ? styles.editLayoutNarrow : {}),
            }}
          >
            <div style={styles.editFilesPane}>
              {detailHeader}
              {tabsAndBody}
            </div>
            <div
              style={{
                ...styles.editReviewPane,
                ...(isNarrow ? styles.editReviewPaneNarrow : {}),
              }}
            >
              <MetadataWorkspacePanel
                row={selectedRow}
                activeTab={activeTab}
                onClose={onCloseReview}
                onPreview={showPreview}
                previewLoading={previewLoading}
                onReindex={onReindex}
                onSaved={onSaved}
                isWorkPool={true}
                source="WORK_POOL"
              />
            </div>
          </div>
        ) : (
          <>
            {detailHeader}
            {tabsAndBody}
          </>
        )}
      </section>
    </div>
  );
}

function MetadataCard({ label, value }) {
  return (
    <div style={styles.metaCard}>
      <div style={styles.metaCardLabel}>{label}</div>
      <div style={styles.metaCardValue}>{value || "—"}</div>
    </div>
  );
}

function AttachmentGridCard({ file, onPreview, previewLoading }) {
  const kind = fileKind(file);
  const colors = fileIconColors(kind);
  const size = formatFileSize(file.size_bytes);

  return (
    <button
      type="button"
      style={styles.gridCard}
      disabled={previewLoading || !file?.path}
      title={file.name || "Untitled"}
      onClick={() => onPreview?.(file.path)}
    >
      <div style={{ ...styles.gridCardIcon, ...colors }}>
        {kind === "image" ? <ImageIcon size={22} /> : <FileText size={22} />}
      </div>
      <div style={styles.gridCardName}>{file.name || "Untitled"}</div>
      <div style={styles.gridCardSize}>{size || "—"}</div>
    </button>
  );
}

function FileCard({ file, label, onPreview, onDownload, previewLoading }) {
  const kind = fileKind(file);
  const size = formatFileSize(file.size_bytes);
  const kindLabel =
    label ||
    (kind === "pdf" ? "PDF" : kind === "image" ? "Image" : "File");

  return (
    <div style={styles.fileCard}>
      <div
        style={{
          ...styles.fileIcon,
          ...fileIconColors(kind),
        }}
      >
        {kind === "image" ? <ImageIcon size={16} /> : <FileText size={16} />}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={styles.fileName}>{file.name || "Untitled"}</div>
        <div style={styles.fileMeta}>
          {kindLabel}
          {size ? ` • ${size}` : ""}
        </div>
      </div>
      <button
        type="button"
        style={styles.iconBtn}
        title="Download"
        onClick={() => onDownload?.(file.path, file.name)}
      >
        <Download size={14} />
      </button>
      <button
        type="button"
        style={styles.iconBtn}
        title="Preview"
        disabled={previewLoading}
        onClick={() => onPreview?.(file.path)}
      >
        <Eye size={14} />
      </button>
    </div>
  );
}

const styles = {
  shell: {
    display: "flex",
    alignItems: "stretch",
    minHeight: 640,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    overflow: "hidden",
  },
  shellNarrow: {
    flexDirection: "column",
    minHeight: 0,
  },
  sidebar: {
    width: 360,
    minWidth: 280,
    flex: "0 0 360px",
    alignSelf: "stretch",
    position: "relative",
    overflow: "hidden",
    borderRight: "1px solid #e2e8f0",
    background: "#fff",
  },
  sidebarNarrow: {
    width: "100%",
    minWidth: 0,
    flex: "0 0 360px",
    height: 360,
    maxHeight: 360,
    borderRight: 0,
    borderBottom: "1px solid #e2e8f0",
  },
  sidebarFill: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  sidebarHeader: {
    padding: "12px 12px 10px",
    borderBottom: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  backBtn: {
    width: 28,
    height: 28,
    border: 0,
    background: "transparent",
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    flexShrink: 0,
    padding: 0,
  },
  sidebarTitle: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
    color: "#0f172a",
    flex: 1,
    minWidth: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  sortSelect: {
    height: 32,
    maxWidth: 128,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 8px",
    background: "#fff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 500,
  },
  filterBtn: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#fff",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  filterBtnActive: {
    border: "1px solid #2563eb",
    color: "#1d4ed8",
    background: "#eff6ff",
  },
  sidebarSearchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
    marginTop: 10,
  },
  threadSearchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    width: "100%",
  },
  threadSearchIcon: {
    position: "absolute",
    left: 10,
    color: "#94a3b8",
    pointerEvents: "none",
  },
  threadSearchInput: {
    width: "100%",
    height: 32,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 8px 0 30px",
    background: "#fff",
    fontSize: 12,
    color: "#0f172a",
    boxSizing: "border-box",
  },
  threadEmpty: {
    fontSize: 13,
    color: "#64748b",
    padding: "16px 8px",
    textAlign: "center",
  },
  filterChipRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  filterChip: {
    height: 32,
    padding: "0 12px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#fff",
    color: "#0f172a",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  filterChipActive: {
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
    fontWeight: 600,
  },
  filtersPopover: {
    position: "fixed",
    zIndex: 80,
    width: 320,
    maxHeight: 420,
    overflow: "auto",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
    padding: 16,
    boxSizing: "border-box",
  },
  filtersTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: 12,
  },
  filtersSection: {
    marginBottom: 14,
  },
  filtersSectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.4,
    color: "#64748b",
    marginBottom: 8,
  },
  filtersRadio: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#334155",
    marginBottom: 6,
    cursor: "pointer",
  },
  filtersDateRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginTop: 8,
  },
  filtersDateInput: {
    height: 32,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 8px",
    fontSize: 12,
  },
  filtersFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 8,
    paddingTop: 12,
    borderTop: "1px solid #e2e8f0",
    position: "sticky",
    bottom: 0,
    background: "#fff",
  },
  filtersClear: {
    border: 0,
    background: "transparent",
    color: "#475569",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    padding: 0,
  },
  filtersApply: {
    height: 32,
    padding: "0 14px",
    border: 0,
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  emailList: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: 10,
    background: "#f8fafc",
  },
  emailCard: {
    width: "100%",
    display: "block",
    textAlign: "left",
    boxSizing: "border-box",
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: 10,
    padding: "10px 12px",
    cursor: "pointer",
    marginBottom: 8,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  },
  emailCardSelected: {
    background: "#eff6ff",
    borderColor: "#93c5fd",
    boxShadow: "inset 3px 0 0 #2563eb, 0 1px 2px rgba(37, 99, 235, 0.12)",
  },
  emailCardBody: {
    minWidth: 0,
  },
  emailCardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4,
  },
  emailCardTime: {
    color: "#64748b",
    fontSize: 11,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  },
  emailCardSubject: {
    fontWeight: 700,
    color: "#0f172a",
    fontSize: 13,
    lineHeight: 1.35,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  emailCardFrom: {
    color: "#2563eb",
    fontSize: 12,
    lineHeight: 1.35,
    marginTop: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  listFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    minHeight: 44,
    padding: "8px 12px",
    borderTop: "1px solid #e2e8f0",
    background: "#fff",
    flexShrink: 0,
  },
  listFooterCount: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  pager: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  pagerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    height: 28,
    padding: "0 6px",
    border: 0,
    background: "transparent",
    color: "#334155",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  pagerBtnDisabled: {
    color: "#cbd5e1",
    cursor: "default",
  },
  pagerCurrent: {
    minWidth: 28,
    height: 28,
    padding: "0 8px",
    borderRadius: 6,
    background: "#2563eb",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  detail: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    background: "#f8fafc",
  },
  editLayout: {
    display: "flex",
    alignItems: "stretch",
    minWidth: 0,
  },
  editLayoutNarrow: {
    flexDirection: "column",
  },
  editFilesPane: {
    flex: 1,
    minWidth: 0,
  },
  editReviewPane: {
    width: 400,
    minWidth: 360,
    maxWidth: 440,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    borderLeft: "1px solid #e2e8f0",
  },
  editReviewPaneNarrow: {
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    borderLeft: 0,
    borderTop: "1px solid #e2e8f0",
  },
  emptyDetail: {
    padding: 32,
    color: "#64748b",
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    padding: 20,
    background: "#fff",
    borderBottom: "1px solid #e2e8f0",
  },
  detailTitleWrap: {
    display: "flex",
    gap: 12,
    minWidth: 0,
  },
  mailBadge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: "#dcfce7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  detailSubject: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.35,
  },
  detailEmailIndex: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    marginBottom: 4,
  },
  editBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 38,
    padding: "0 14px",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    background: "#fff",
    color: "#0f172a",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  infoBox: {
    margin: 0,
    padding: "10px 14px",
    background: "#f1f5f9",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  infoRow: {
    display: "flex",
    gap: 16,
    fontSize: 13,
  },
  infoLabel: {
    width: 110,
    flexShrink: 0,
    color: "#64748b",
    fontWeight: 600,
  },
  infoValue: {
    color: "#0f172a",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  copyBtn: {
    border: 0,
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    padding: 0,
    display: "inline-flex",
  },
  tabs: {
    display: "flex",
    gap: 18,
    padding: "0 20px",
    borderBottom: "1px solid #e2e8f0",
    background: "#fff",
  },
  tab: {
    border: 0,
    background: "transparent",
    padding: "12px 0",
    color: "#64748b",
    fontWeight: 600,
    cursor: "pointer",
  },
  tabActive: {
    color: "#2563eb",
    boxShadow: "inset 0 -2px 0 #2563eb",
  },
  tabBody: {
    padding: 20,
  },
  previewFrame: {
    position: "relative",
    height: 520,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    overflow: "auto",
    display: "block",
  },
  navArrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 36,
    height: 36,
    border: 0,
    borderRadius: "50%",
    background: "rgba(15, 23, 42, 0.75)",
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    padding: 0,
  },
  navArrowLeft: {
    left: 12,
  },
  navArrowRight: {
    right: 12,
  },
  previewCounter: {
    position: "absolute",
    bottom: 12,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(15, 23, 42, 0.75)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
    zIndex: 2,
    whiteSpace: "nowrap",
  },
  previewIframe: {
    width: "100%",
    minHeight: "100%",
    height: "100%",
    border: "none",
    background: "#fff",
    display: "block",
  },
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    display: "block",
    background: "#0f172a",
  },
  previewStatus: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    fontSize: 14,
    padding: 24,
    textAlign: "center",
  },
  muted: {
    color: "#64748b",
    fontSize: 13,
    marginBottom: 16,
  },
  attachHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    margin: "0 0 16px",
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: "#fff",
    paddingBottom: 4,
  },
  attachTitle: {
    fontWeight: 700,
    color: "#334155",
    fontSize: 14,
  },
  attachHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexShrink: 0,
  },
  attachActions: {
    display: "flex",
    gap: 16,
  },
  attachPanel: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 16,
    background: "#fff",
    height: 520,
    overflow: "auto",
    boxSizing: "border-box",
  },
  attachGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  attachGridNarrow: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  viewToggle: {
    display: "inline-flex",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
  },
  viewToggleBtn: {
    width: 32,
    height: 32,
    border: 0,
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  },
  viewToggleBtnActive: {
    background: "#eff6ff",
    color: "#2563eb",
  },
  gridCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 8,
    padding: "20px 12px 16px",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#fff",
    cursor: "pointer",
    minWidth: 0,
    font: "inherit",
  },
  gridCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  gridCardName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
  },
  gridCardSize: {
    fontSize: 12,
    color: "#94a3b8",
  },
  metaPanel: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "auto",
    maxHeight: 520,
    overflow: "auto",
    boxSizing: "border-box",
  },
  metaCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 12px",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  },
  metaCardLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
    color: "#94a3b8",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  metaCardValue: {
    fontSize: 13,
    fontWeight: 600,
    color: "#0f172a",
    overflowWrap: "anywhere",
  },
  textLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: 0,
    background: "transparent",
    color: "#2563eb",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
  },
  fileCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 12,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    marginBottom: 10,
  },
  fileIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  fileName: {
    fontWeight: 600,
    color: "#0f172a",
    fontSize: 13,
    overflowWrap: "anywhere",
  },
  fileMeta: {
    color: "#64748b",
    fontSize: 12,
    marginTop: 2,
  },
  iconBtn: {
    width: 32,
    height: 32,
    border: "1px solid #e2e8f0",
    background: "#fff",
    borderRadius: 8,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pill: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  pillPending: {
    background: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fdba74",
  },
  pillVerified: {
    background: "#f0fdf4",
    color: "#15803d",
    border: "1px solid #86efac",
  },
};
